import { useEffect, useRef, useState } from 'react';
import { OPPORTUNITY_HEADLINE_NOTE, tgFacilityOpportunity, tgProgramCatalog, type TgFacilityOpportunity, type TgProgramCatalog } from '@aithora/shared';
import { useSession } from '../../../session/SessionContext';
import { useQuery } from '../../../useQuery';
import { sx } from '../../../ui/designStyles';

// A facility's reimbursement opportunity, code by code, with its state pool, and the
// all-programs catalog: restored from the production console (simulate_panel.dart, the
// opportunity breakdown and its PDF leave-behind; targeting_view.dart, "View all
// programs"). Not in the V3 design: a dialog off the inspector's opportunity figure.
// Every dollar is MODELED; an estimated rate says so.

const $ = (n: number) => '$' + Math.round(n).toLocaleString('en-US');
const esc = (s: unknown) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
const CELL = 'font-size:16px;color:#22272E;padding:5px 0;border-bottom:1px solid rgba(34,39,46,.07);';
const BTN = 'display:inline-flex;align-items:center;height:30px;padding:0 12px;border-radius:7px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;';

/** Where the residents figure comes from: the CMS catalog's census, or an estimate. */
export const censusWord = (o: Pick<TgFacilityOpportunity, 'censusEstimated'>) => (o.censusEstimated ? 'estimated census' : 'CMS census');

export interface BreakdownRow {
  key: string;
  code: string;
  label: string;
  eligible: number | null;
  /** Every eligible resident billed every period (the "at full use" headline's part). */
  full: number;
  /** At the national billing rate (the national-rate headline's part); null for the state pool, which is not Part B. */
  national: number | null;
}

/**
 * The breakdown's rows, so each column sums to its headline: "At full use" sums to
 * opportunityAnnual (every code at full capture plus the state pool, up to its amount),
 * "At national rate" sums to reimbursementAnnual (Part B only).
 */
export function breakdownRows(o: TgFacilityOpportunity): BreakdownRow[] {
  const rows: BreakdownRow[] = o.codes
    .filter((c) => (c.atFullCapture ?? 0) > 0 || c.annual > 0)
    .map((c) => ({
      key: c.code,
      code: c.code,
      label: c.label + (c.rateVerified ? '' : ' · estimated rate') + (c.primary ? ' · primary' : ''),
      eligible: c.eligibleResidents,
      full: c.atFullCapture ?? c.annual,
      national: c.annual,
    }));
  if (o.statePool && o.statePool.annualUpTo > 0) {
    rows.push({ key: 'pool', code: o.statePool.state, label: `${o.statePool.pool} (state program, up to)`, eligible: null, full: o.statePool.annualUpTo, national: null });
  }
  return rows;
}

/** The leave-behind: a print-ready page the browser saves as a PDF. */
export function opportunityPrintHtml(o: TgFacilityOpportunity): string {
  const rows = breakdownRows(o)
    .map((r) => `<tr><td>${esc(r.code)}</td><td>${esc(r.label)}</td><td>${r.eligible ?? ''}</td><td>${$(r.full)}</td><td>${r.national == null ? 'not Part B' : $(r.national)}</td></tr>`).join('');
  const total = `<tr><th></th><th>Total</th><th></th><th>${$(o.opportunityAnnual)}</th><th>${$(o.reimbursementAnnual)}</th></tr>`;
  const pool = o.statePool ? `<p>${esc(o.statePool.pool)} (${esc(o.statePool.state)}): ${esc(o.statePool.note.replace(/\.$/, ''))}. Counted at full use, up to ${$(o.statePool.annualUpTo)}; not part of the national-rate figure.</p>` : '';
  const typical = `<p>At the national billing rate: ${$(o.reimbursementAnnual)} a year.</p>`;
  return `<!doctype html><html><head><meta charset="utf-8"><title>Reimbursement opportunity, ${esc(o.name)}</title>
<style>body{font-family:Archivo,Arial,sans-serif;color:#22272E;margin:32px}h1{font-size:20px}table{border-collapse:collapse;width:100%}td,th{border-bottom:1px solid #ddd;padding:6px;text-align:left;font-size:14px}.m{color:#54606C;font-size:14px}</style></head>
<body><h1>Reimbursement opportunity: ${esc(o.name)}</h1>
<p>${esc([o.state, o.type].filter(Boolean).join(' · '))} · ${o.residents} residents (${censusWord(o)}) · ${o.medicareEligible} Medicare eligible</p>
<p><b>${$(o.opportunityAnnual)} a year</b> MODELED at full use (Medicare Part B plus any state program, ${$(o.perResidentPerYear)} per resident). ${esc(OPPORTUNITY_HEADLINE_NOTE)}</p>${typical}
<table><tr><th>Code</th><th>Program</th><th>Eligible residents</th><th>At full use, per year</th><th>At national rate, per year</th></tr>${rows}${total}</table>${pool}
<p class="m">MODELED by Aithora from the CMS catalog. ${o.caveats.map(esc).join(' ')}</p></body></html>`;
}

export function OpportunityBreakdown({ facilityId, onClose }: { facilityId: string; onClose: () => void }) {
  const { client } = useSession();
  const [all, setAll] = useState(false);
  const opp = useQuery<TgFacilityOpportunity>((signal) => tgFacilityOpportunity(client, { params: { id: facilityId }, signal }), [client, facilityId]);
  const cat = useQuery<TgProgramCatalog>((signal) => tgProgramCatalog(client, { signal }), [client], { enabled: all });
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeRef.current?.focus();
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  const o = opp.data;
  const print = () => {
    if (!o) return;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(opportunityPrintHtml(o));
    w.document.close();
    w.focus();
    w.print();
  };
  return (
    <div role="presentation" onClick={onClose} style={sx('position:fixed;inset:0;background:rgba(34,39,46,.32);display:flex;align-items:center;justify-content:center;z-index:60;padding:16px;')}>
      <div role="dialog" aria-modal="true" aria-label="Reimbursement opportunity" data-testid="tg-opportunity" onClick={(e) => e.stopPropagation()}
        style={sx('box-sizing:border-box;width:min(720px,100%);max-height:86vh;overflow-y:auto;background:#FCFAF4;border-radius:14px;padding:18px 20px;box-shadow:0 12px 40px rgba(34,39,46,.25);')}>
        <div style={sx('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;')}>
          <span style={sx('font-size:19px;font-weight:800;color:#22272E;')}>{all ? 'All programs' : 'Reimbursement opportunity'}</span>
          {/* Shown to everyone, Sales included: every dollar here is modeled (AF-1659). */}
          <span style={sx('font-size:14px;font-weight:700;letter-spacing:.06em;color:#264C7E;background:rgba(47,92,151,.12);border-radius:4px;padding:1px 6px;')}>MODELED</span>
          <span style={sx('flex:1;')} />
          {!all && o && <a href="#" onClick={(e) => { e.preventDefault(); print(); }} style={sx(BTN + 'background:#2F5C97;color:#fff;')}>PDF</a>}
          <a href="#" onClick={(e) => { e.preventDefault(); setAll(!all); }} style={sx(BTN + 'border:1px solid rgba(34,39,46,.18);color:#3C454F;')}>{all ? 'This facility' : 'All programs'}</a>
          <button ref={closeRef} type="button" onClick={onClose} style={sx(BTN + 'border:1px solid rgba(34,39,46,.18);background:transparent;color:#3C454F;cursor:pointer;font-family:inherit;')}>Close</button>
        </div>
        {!all && (
          <>
            {!o && !opp.error && <div style={sx('font-size:16px;color:#54606C;padding:12px 0;')}>Modeling the code stack…</div>}
            {opp.error && <div role="alert" style={sx('font-size:16px;color:#B3261E;padding:12px 0;')}>{opp.error.message}</div>}
            {o && (
              <>
                <div style={sx('font-size:16px;color:#3C454F;margin-top:6px;')} title={OPPORTUNITY_HEADLINE_NOTE}>
                  {`${o.name ?? ''} · ${o.residents} residents (${censusWord(o)}) · ${o.medicareEligible} Medicare eligible · ${$(o.opportunityAnnual)} a year at full use · ${$(o.reimbursementAnnual)} at the national billing rate`}
                </div>
                <div style={sx('font-size:14px;color:#54606C;margin-top:4px;')}>{OPPORTUNITY_HEADLINE_NOTE}</div>
                {/* Two money columns, each summing to its headline figure (the Total row). */}
                <div data-testid="tg-opp-table" style={sx('display:grid;grid-template-columns:62px minmax(0,1fr) 62px 104px 104px;gap:0 10px;margin-top:10px;')}>
                  {['Code', 'Program', 'Eligible', 'At full use / yr', 'At national rate / yr'].map((h, i) => (
                    <span key={h} style={sx('font-size:14px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#54606C;padding-bottom:4px;' + (i >= 2 ? 'text-align:right;' : ''))}>{h}</span>
                  ))}
                  {breakdownRows(o).map((r) => (
                    <div key={r.key} data-testid="tg-opp-row" style={sx('display:contents;')}>
                      <span style={sx(CELL + 'font-weight:700;color:#2F5C97;font-variant-numeric:tabular-nums;')}>{r.code}</span>
                      <span style={sx(CELL)} title={o.codes.find((c) => c.code === r.key)?.gate}>{r.label}</span>
                      <span style={sx(CELL + 'text-align:right;font-variant-numeric:tabular-nums;')}>{r.eligible ?? ''}</span>
                      <span data-col="full" style={sx(CELL + 'text-align:right;font-weight:700;font-variant-numeric:tabular-nums;')}>{$(r.full)}</span>
                      <span data-col="national" style={sx(CELL + 'text-align:right;font-variant-numeric:tabular-nums;' + (r.national == null ? 'color:#54606C;' : ''))}>{r.national == null ? 'not Part B' : $(r.national)}</span>
                    </div>
                  ))}
                  <span style={sx(CELL + 'border-bottom:0;')} />
                  <span style={sx(CELL + 'border-bottom:0;font-weight:800;')}>Total</span>
                  <span style={sx(CELL + 'border-bottom:0;')} />
                  <span data-testid="tg-opp-total-full" style={sx(CELL + 'border-bottom:0;text-align:right;font-weight:800;font-variant-numeric:tabular-nums;')}>{$(o.opportunityAnnual)}</span>
                  <span data-testid="tg-opp-total-national" style={sx(CELL + 'border-bottom:0;text-align:right;font-weight:800;font-variant-numeric:tabular-nums;')}>{$(o.reimbursementAnnual)}</span>
                </div>
                {o.statePool && (
                  <div style={sx('font-size:16px;color:#3C454F;margin-top:10px;')}>{`${o.statePool.pool} (${o.statePool.state}): ${o.statePool.note.replace(/\.$/, '')}. Counted at full use, up to ${$(o.statePool.annualUpTo)} a year; not part of the national-rate figure.`}</div>
                )}
                <div style={sx('font-size:14px;color:#54606C;margin-top:8px;line-height:1.45;')}>{o.caveats.join(' ')}</div>
              </>
            )}
          </>
        )}
        {all && (
          <>
            {!cat.data && <div style={sx('font-size:16px;color:#54606C;padding:12px 0;')}>{cat.error ? cat.error.message : 'Loading the catalog…'}</div>}
            {cat.data && (
              <>
                <div style={sx('font-size:14px;color:#54606C;margin-top:6px;line-height:1.45;')}>{cat.data.note}</div>
                <div data-testid="tg-catalog" style={sx('margin-top:8px;')}>
                  {cat.data.codes.map((c) => (
                    <div key={c.code} style={sx(CELL + 'display:flex;gap:10px;')}>
                      <span style={sx('flex:0 0 64px;font-weight:700;color:#2F5C97;')}>{c.code}</span>
                      <span style={sx('flex:1 1 auto;min-width:0;')}>{c.label + (c.rateVerified === false ? ' · estimated rate' : '')}</span>
                      {typeof c.rate === 'number' && <span style={sx('font-variant-numeric:tabular-nums;')}>{'$' + c.rate.toFixed(2) + ' / ' + (c.frequency === 'annual' ? 'yr' : 'mo')}</span>}
                    </div>
                  ))}
                </div>
                <div style={sx('font-size:16px;font-weight:800;color:#22272E;margin-top:12px;')}>State pools</div>
                {cat.data.statePools.map((p) => (
                  <div key={p.state + p.pool} style={sx(CELL + 'display:flex;gap:10px;')}>
                    <span style={sx('flex:0 0 40px;font-weight:700;')}>{p.state}</span>
                    <span style={sx('flex:1 1 auto;min-width:0;')}>{p.pool}</span>
                    <span style={sx('font-variant-numeric:tabular-nums;')}>{'up to ' + $(p.annualUpTo)}</span>
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
